import { supabase } from '../lib/supabase';

const HEARTBEAT_INTERVAL = 30_000; // 30 seconds
const ACTIVE_THRESHOLD = 90_000;   // 90 seconds — device considered active if heartbeat within this window
const MAX_DEVICES_DEFAULT = 3;

let heartbeatTimer = null;
let currentDeviceId = null;

function getOrCreateDeviceId() {
  let id = localStorage.getItem('pension_device_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('pension_device_id', id);
  }
  return id;
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

// המשתמש מה-session המקומי — getUser() עושה קריאת רשת ל-/auth/v1/user בכל
// heartbeat (כל 30 שניות) ונכשל ב-403 כשה-session בוטל בצד השרת. ל-RLS על
// user_devices מספיק ה-JWT עצמו, אז אין צורך באימות שרת נפרד לכל פעולה.
async function getSessionUser() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user || null;
}

export async function registerDevice() {
  const user = await getSessionUser();
  if (!user) return { allowed: false, reason: 'no_user' };

  currentDeviceId = getOrCreateDeviceId();
  const deviceName = getDeviceName();

  const { data: devices } = await supabase
    .from('user_devices')
    .select('*')
    .eq('user_id', user.id)
    .order('last_active_at', { ascending: false });

  const existingDevice = devices?.find(d => d.device_id === currentDeviceId);
  const now = Date.now();

  // Concurrent access on multiple devices is allowed for the same user.
  // Only the total number of registered devices is limited (max_devices).

  const { data: profile } = await supabase
    .from('profiles')
    .select('max_devices')
    .eq('id', user.id)
    .single();

  const maxDevices = profile?.max_devices || MAX_DEVICES_DEFAULT;

  if (!existingDevice && (devices || []).length >= maxDevices) {
    return {
      allowed: false,
      reason: 'max_devices',
      devices: devices,
      maxDevices,
    };
  }

  await supabase
    .from('user_devices')
    .upsert(
      {
        user_id: user.id,
        device_id: currentDeviceId,
        device_name: deviceName,
        last_active_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,device_id' }
    );

  startHeartbeat();
  return { allowed: true, deviceId: currentDeviceId };
}

async function sendHeartbeat() {
  const user = await getSessionUser();
  if (!user || !currentDeviceId) return;

  await supabase
    .from('user_devices')
    .update({ last_active_at: new Date().toISOString() })
    .eq('user_id', user.id)
    .eq('device_id', currentDeviceId);
}

function startHeartbeat() {
  stopHeartbeat();
  sendHeartbeat();
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
}

export async function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  // Mark device as inactive immediately so other devices aren't blocked
  if (currentDeviceId && supabase) {
    try {
      const user = await getSessionUser();
      if (user) {
        await supabase
          .from('user_devices')
          .update({ last_active_at: new Date(0).toISOString() }) // epoch = definitely inactive
          .eq('user_id', user.id)
          .eq('device_id', currentDeviceId);
      }
    } catch {}
  }
}

export async function removeDevice(deviceId) {
  const user = await getSessionUser();
  if (!user) return;

  await supabase
    .from('user_devices')
    .delete()
    .eq('user_id', user.id)
    .eq('device_id', deviceId);
}

export async function getDevices() {
  const user = await getSessionUser();
  if (!user) return [];

  const { data } = await supabase
    .from('user_devices')
    .select('*')
    .eq('user_id', user.id)
    .order('last_active_at', { ascending: false });

  return data || [];
}
